// Recebimentos do paciente (VA) — puro.
//
// TRÊS VALORES, TRÊS PAPÉIS. Confundi-los é contar o mesmo dinheiro duas vezes:
//
//   valorFechado  — total do tratamento. É a VENDA: metas, ranking, painel.
//   valorEntrada  — pago no ato (Pix, dinheiro, cartão). GERENCIAL, do dia.
//                   NÃO entra em comissão.
//   valorRecebido — soma dos recebimentos desta subcoleção, que vêm da
//                   importação do Careflow. ÚNICA base da comissão futura.
//
// A entrada NÃO é um recebimento: ela reaparece na planilha do Careflow como
// parcela liquidada e, contada nos dois lugares, entraria duas vezes na
// comissão. Por isso o SALDO desconta só o `valorRecebido` — enquanto a
// importação não confirma a parcela, a entrada é caixa, não recebimento.

export const FORMAS_RECEBIMENTO = [
  { id: 'dinheiro', label: 'Dinheiro' },
  { id: 'pix', label: 'Pix' },
  { id: 'cartao', label: 'Cartão' },
  { id: 'boleto', label: 'Boleto' },
  { id: 'outro', label: 'Outro' },
];

export const formaPorId = (id) =>
  FORMAS_RECEBIMENTO.find((f) => f.id === id) ?? { id, label: id || '—' };

export const FONTES = ['manual', 'importacao'];

/** Número a partir de texto brasileiro ("1.250,50") ou número. */
export function paraNumero(valor) {
  if (typeof valor === 'number') return Number.isFinite(valor) ? valor : 0;
  const texto = String(valor ?? '').trim();
  if (!texto) return 0;
  // Tira separador de milhar e troca a vírgula decimal por ponto.
  const limpo = texto.replace(/\s/g, '').replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.');
  const n = Number(limpo);
  return Number.isFinite(n) ? n : 0;
}

const paraData = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const somarRecebimentos = (lista = []) =>
  (lista ?? []).reduce((t, r) => t + paraNumero(r?.valor), 0);

/**
 * Saldo em aberto do contrato: total − RECEBIDO.
 *
 * ⚠ NÃO desconta a entrada. Quando a parcela da entrada liquidar no Careflow,
 * ela entra como recebimento; descontá-la aqui também abateria o mesmo dinheiro
 * duas vezes. Nunca negativo: pagar acima do contrato mostra 0 (e o painel diz
 * "recebido acima do contrato").
 */
export function saldoDoLead(lead) {
  if (lead?.etapa !== 'fechado') return 0;
  const total = paraNumero(lead?.valorFechado);
  const recebido = paraNumero(lead?.valorRecebido);
  return Math.max(0, total - recebido);
}

export const temSaldoAberto = (lead) => saldoDoLead(lead) > 0;

export function percentualRecebido(lead) {
  const total = paraNumero(lead?.valorFechado);
  if (total <= 0) return 0;
  return Math.min(100, (paraNumero(lead?.valorRecebido) / total) * 100);
}

/** Recebido acima do contrato — o painel avisa em vez de esconder. */
export const recebidoAcimaDoContrato = (lead) =>
  paraNumero(lead?.valorRecebido) > paraNumero(lead?.valorFechado);

/**
 * ★ FRONTEIRA DE MÊS (VC) — normaliza a data para MEIO-DIA do seu próprio dia.
 *
 * O recebimento é dinheiro, e o mês em que ele cai decide de quem é a comissão.
 * No relatório real de agosto/2026 há 22 parcelas em 31/08 e 1 em 01/09: 23
 * pagamentos exatamente na borda.
 *
 * O perigo concreto é uma data gravada à MEIA-NOITE UTC. `new Date('2026-09-01')`
 * é `01/09 00:00Z`, que em São Paulo é **31 de agosto**: um pagamento de setembro
 * entraria na comissão de agosto. Os dois caminhos de escrita de hoje já usam
 * meio-dia, mas nada impedia o próximo chamador de passar uma data crua — esta
 * função fecha a porta no MONTADOR, que é por onde todos passam.
 *
 * Meio-dia dá 12 horas de folga para cada lado; o Brasil usa 3.
 */
export function aoMeioDia(data) {
  if (!data) return null;
  const d = new Date(data);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(12, 0, 0, 0);
  return d;
}

/** 'AAAA-MM' pelo calendário LOCAL — nunca por `toISOString()`, que é UTC. */
export function mesDaData(data) {
  const d = paraData(data);
  if (!d) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Monta o documento de um recebimento — MONTADOR ÚNICO, do lançamento manual
 * (VA) e da importação (VA-2). Valida antes de qualquer escrita.
 */
export function montarRecebimento(
  { clinicaId, leadId, data, mes = null, valor, forma = 'outro', fonte = 'manual', idExterno = null, observacao = '' },
  autor,
  { agora = null } = {},
) {
  if (!clinicaId) throw new Error('Recebimento sem clínica.');
  const quando = aoMeioDia(paraData(data));
  if (!quando) throw new Error('Informe uma data válida para o recebimento.');
  const numero = paraNumero(valor);
  if (!(numero > 0)) throw new Error('O valor do recebimento precisa ser maior que zero.');
  if (!FORMAS_RECEBIMENTO.some((f) => f.id === forma)) {
    throw new Error(`Forma de recebimento desconhecida: ${forma}.`);
  }

  return {
    data: quando,
    // ★ O MÊS DO DINHEIRO, gravado como texto. Ver `aoMeioDia` e `mesDaData`.
    mes: mes || mesDaData(quando),
    valor: numero,
    forma,
    // De onde veio: 'manual' (gestor lançou) ou 'importacao' (Careflow, VA-2).
    fonte: FONTES.includes(fonte) ? fonte : 'manual',
    // Identificador do registro no sistema de origem, quando houver. É a chave
    // de dedup preferida da importação.
    idExterno: idExterno ?? null,
    observacao: String(observacao ?? '').trim(),
    leadId: leadId ?? null,
    clinicaId,
    criadoPorId: autor?.id ?? 'sistema',
    criadoPorNome: autor?.nome ?? 'Sistema',
    criadoEm: agora,
  };
}

/** 'aaaa-mm-dd' no fuso local — a chave de dedup é lida por dia, não por instante. */
export function chaveDia(data) {
  const d = paraData(data);
  if (!d) return '';
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
}

/**
 * Chave de deduplicação (usada pela importação, VA-2).
 *
 * Com `idExterno`, é ele — dois lançamentos do mesmo registro do Careflow são o
 * mesmo dinheiro, ainda que valor ou data tenham sido corrigidos na origem.
 * Sem ele, `leadId|dia|centavos`: o mesmo paciente, no mesmo dia, pelo mesmo
 * valor. Centavos (inteiro) para `100` e `100,00` darem a mesma chave.
 */
export function chaveDedup(recebimento) {
  if (recebimento?.idExterno) return `ext:${recebimento.idExterno}`;
  const centavos = Math.round(paraNumero(recebimento?.valor) * 100);
  return `${recebimento?.leadId ?? ''}|${chaveDia(recebimento?.data)}|${centavos}`;
}

export const ehDuplicado = (novo, existentes = []) => {
  const chave = chaveDedup(novo);
  return (existentes ?? []).some((r) => chaveDedup(r) === chave);
};

/** A mais recente das duas datas (qualquer uma pode ser nula). */
export function maiorData(a, b) {
  const da = paraData(a);
  const db = paraData(b);
  if (!da) return db;
  if (!db) return da;
  return da >= db ? da : db;
}

/** Data do recebimento mais recente da lista, ou null. */
export const ultimoRecebimentoDe = (lista = []) =>
  (lista ?? []).reduce((maior, r) => maiorData(maior, r?.data), null);
